// Client Ordering, turning messy client order input into clean line items.
//
// Clients send orders as pasted email text, Excel, or CSV. This module
// reads those into { description, quantity } rows the user can review,
// match to the catalog, and save. It is intentionally heuristic (no AI) so it
// works fully in the browser today; an AI "standardize anything" path can be
// added later behind the same line-item shape.

import { xlsxToMatrix, csvToMatrix } from './orderParse';

// Lines that are clearly email chrome rather than order items, left out of the
// order. `annotatePastedText` below is what lets the screen SHOW that they were.
const NOISE = [
  /^(hi|hello|hey|good (morning|afternoon|evening))\b/i,
  /^(thanks|thank you|thx|best|regards|sincerely|cheers|warm regards)\b/i,
  /^(sent from my )/i,
  /^(on .+wrote:)/i,
  /^(from|to|cc|bcc|subject|sent|date)\s*:/i,
  /^[-_=]{3,}$/,                 // separator lines
  /^\s*$/,
];

// Strip a leading list marker ("1) ", "1. ", "- ", "* ", "• ") so an enumerator
// isn't mistaken for a quantity. Returns the remainder.
function stripListMarker(s) {
  return s.replace(/^\s*(?:\d{1,3}[.)]\s+|[-*•·]\s+)/, '').trim();
}

// Trim surrounding separators/punctuation left behind after removing a qty token.
const cleanDesc = (d) => (d || '').replace(/\s{2,}/g, ' ').replace(/^[\s\-–—:|.,]+/, '').replace(/[\s\-–—:|]+$/, '').trim();

// Pull a quantity out of a single item line using a few common patterns, and
// return the description with that quantity token removed. qtyFound=false means
// no explicit quantity was present (caller can default to 1).
export function extractQuantity(line) {
  const s = (line || '').trim();
  let m;
  const found = (qty, desc) => ({ quantity: parseInt(qty, 10), description: cleanDesc(desc), qtyFound: true });

  // "qty 3", "quantity: 3", "count = 3"
  if ((m = s.match(/\b(?:qty|quantity|count)\b\s*[:=#-]?\s*(\d+)/i))) {
    return found(m[1], s.replace(m[0], ' '));
  }
  // leading "3x ", "3 x ", "3×"
  if ((m = s.match(/^(\d+)\s*[x×]\s+/i))) {
    return found(m[1], s.slice(m[0].length));
  }
  // trailing "... x3", "... x 3"
  if ((m = s.match(/\s[x×]\s*(\d+)\s*$/i))) {
    return found(m[1], s.slice(0, m.index));
  }
  // trailing count with optional unit: "... - 3", "... 3 ea", "... (3)", "... 3 cases"
  if ((m = s.match(/[\s\-–—(]+(\d+)\s*(ea|each|pcs?|pc|units?|cs|cases?|pr|pairs?|box(?:es)?)?\s*\)?\s*$/i))) {
    return found(m[1], s.slice(0, m.index));
  }
  // leading bare number then text: "3 Walking Boot" (avoid descriptions that
  // legitimately start with a number like "3M Tape" by requiring a space + letter)
  if ((m = s.match(/^(\d{1,4})\s+(?=[A-Za-z])/))) {
    return found(m[1], s.slice(m[0].length));
  }
  return { quantity: null, description: cleanDesc(s), qtyFound: false };
}

/*
  Every line of what the client sent, and whether it became an order line.

  **Group G's approved drawing shows the client's email with its greeting and
  sign-off greyed out**, and a sentence under it saying so. That is not
  decoration: the parser drops lines, and a person handing an order to a
  colleague needs to see that "Could we get the following for next week please"
  was read as chatter rather than as an item. Without it the only honest reading
  of the screen is "four of these five lines are here and I do not know which".

  **Writing it found two lines the parser was keeping and should not have been.**
  On the drawing's own example email, an order came out with six lines: the four
  items, the sentence introducing them, and the word "Andrea", which is who sent
  it. Somebody has been deleting those by hand on every order since this screen
  was built, or worse, not noticing.

  So two rules, both of them about how an email is shaped rather than about what
  is in it:

  - **Nothing below a sign-off is an order line.** "Thanks," ends the message;
    what follows is a name, a job title and a phone number.
  - **A line ending in a colon with no quantity on it is a lead-in**, not an
    item. "Could we get the following for next week please:" introduces the
    order rather than being part of it.
*/
const SIGN_OFF = /^(thanks|thank you|thx|best|regards|sincerely|cheers|warm regards|many thanks|kind regards)\b/i;

function readLines(text) {
  const out = [];
  let done = false;   // past the sign-off: everything after it is a signature
  for (const rawLine of (text || '').split(/\r?\n/)) {
    const trimmed = rawLine.trim();
    if (!trimmed) { out.push({ text: rawLine, used: false, blank: true }); continue; }
    if (done) { out.push({ text: rawLine, used: false, after: true }); continue; }
    if (SIGN_OFF.test(trimmed)) { done = true; out.push({ text: rawLine, used: false }); continue; }
    if (NOISE.some(re => re.test(trimmed))) { out.push({ text: rawLine, used: false }); continue; }
    const body = stripListMarker(trimmed);
    if (!body) { out.push({ text: rawLine, used: false }); continue; }
    const { quantity, description, qtyFound } = extractQuantity(body);
    if (!description) { out.push({ text: rawLine, used: false }); continue; }
    // A sentence that introduces the order rather than being part of it.
    if (!qtyFound && /:$/.test(trimmed)) { out.push({ text: rawLine, used: false }); continue; }
    out.push({
      text: rawLine, used: true,
      item: { raw_text: trimmed, description, quantity: qtyFound ? quantity : 1, qtyFound },
    });
  }
  return out;
}

export function annotatePastedText(text) {
  return readLines(text).map(({ item, ...rest }) => rest);
}

// Parse pasted email/text into line items, one per meaningful line.
export function parsePastedText(text) {
  return readLines(text).filter(l => l.used).map(l => l.item);
}

const DESC_HEADER   = /descri|item\s*name|product|model|name|item(?!\s*#|\s*no|\s*num)/i;
// Note: \bunits\b (plural) so we don't match a "Unit Cost" / "Unit Price" column.
const QTY_HEADER    = /\bqty\b|quantit|count|\bunits\b|cases?|pcs?|\beach\b|ordered|order\s*qty|amount/i;
const NUM_HEADER    = /item\s*(#|no\.?|num)|^item$|sku|part\s*(#|no|num)|catalog/i;
const VENDOR_HEADER = /vendor|manufacturer|\bmfr\b|supplier|brand/i;
const UOM_HEADER    = /\buom\b|unit|\bea\b|each/i;
const NUMERIC = /^\$?\d+(\.\d+)?$/;

// Parse a spreadsheet matrix (array of string rows) into order line items.
//
// Real client order forms have a title block, a "Location:" row, a header row
// several rows down, category divider rows, and the full catalog listed with a
// Qty column the client fills in only for what they want. So we: locate the real
// header row by scoring header keywords, map the Item #/Qty/Description columns,
// skip dividers/blanks, and — when a Qty column exists — import only rows whose
// Qty is filled (the items actually being ordered). Also pulls a "Location:"
// value as a suggested client name.
export function parseSpreadsheetMatrix(matrix) {
  const grid = (matrix || []).map(r => (r || []).map(c => String(c ?? '').trim()));
  if (!grid.length) return { lines: [], headers: [], mapping: {}, location: '' };

  // "Location:" / "Account:" gives the suggested client name.
  let location = '';
  for (let i = 0; i < Math.min(grid.length, 12); i++) {
    const r = grid[i];
    const li = r.findIndex(c => /^(location|account|facility|customer|client)\b/i.test(c));
    if (li >= 0) {
      const val = r.slice(li + 1).find(c => c && !/^date\b/i.test(c) && !/:$/.test(c));
      if (val) { location = val; break; }
    }
  }

  // Find the header row: score each of the first rows by header keywords and
  // require at least an item-number or description header.
  const scoreRow = (r) => r.reduce((n, c) => n +
    (NUM_HEADER.test(c) ? 2 : 0) + (QTY_HEADER.test(c) ? 2 : 0) +
    (DESC_HEADER.test(c) ? 1 : 0) + (VENDOR_HEADER.test(c) ? 1 : 0) + (UOM_HEADER.test(c) ? 1 : 0), 0);
  let headerIdx = -1, bestScore = 0;
  for (let i = 0; i < Math.min(grid.length, 25); i++) {
    const s = scoreRow(grid[i]);
    if (s > bestScore && grid[i].some(c => NUM_HEADER.test(c) || DESC_HEADER.test(c))) { bestScore = s; headerIdx = i; }
  }

  const headers = headerIdx >= 0 ? grid[headerIdx] : [];
  const findCol = (re) => headers.findIndex(h => re.test(h));
  let numCol  = findCol(NUM_HEADER);   // item number column (the key); -1 if none
  let qtyCol  = findCol(QTY_HEADER);
  let descCol = findCol(DESC_HEADER);

  const dataRows = grid.slice(headerIdx >= 0 ? headerIdx + 1 : 0);

  // Description fallback: the most text-heavy column that isn't item#/qty.
  if (descCol < 0) {
    const widths = {};
    dataRows.forEach(r => r.forEach((c, i) => { if (i !== numCol && i !== qtyCol && /[A-Za-z]/.test(c)) widths[i] = (widths[i] || 0) + c.length; }));
    const top = Object.keys(widths).sort((a, b) => widths[b] - widths[a])[0];
    if (top != null) descCol = parseInt(top, 10);
  }
  // Qty fallback: a column that is entirely numeric across non-empty values.
  if (qtyCol < 0) {
    for (let i = 0; i < (headers.length || (dataRows[0] || []).length); i++) {
      if (i === numCol || i === descCol) continue;
      const vals = dataRows.map(r => (r[i] || '').trim()).filter(Boolean);
      if (vals.length && vals.every(v => NUMERIC.test(v))) { qtyCol = i; break; }
    }
  }

  const hasQtyCol = qtyCol >= 0;
  const lines = [];
  for (const r of dataRows) {
    const itemNum = numCol >= 0 ? (r[numCol] || '').trim() : '';
    const description = descCol >= 0 ? (r[descCol] || '').trim() : '';
    const qRaw = hasQtyCol ? (r[qtyCol] || '').trim() : '';
    const qty = NUMERIC.test(qRaw) ? Number(qRaw.replace(/^\$/, '')) : null;

    if (!itemNum && !description) continue;            // blank row
    if (hasQtyCol) {
      if (qty == null || qty <= 0) continue;           // only the rows the client actually ordered
    } else if (!description && !/\d/.test(itemNum)) {
      continue;                                        // category/divider row (e.g. "Cervical")
    }
    lines.push({
      raw_text: r.filter(Boolean).join(' | '),
      description: description || itemNum,
      item_number: itemNum,
      quantity: qty == null ? 1 : qty,
      qtyFound: qty != null,
    });
  }
  return { lines, headers, mapping: { numCol, qtyCol, descCol }, location };
}

// Parse an uploaded client file (.xlsx/.xls or .csv) into line items.
export function parseClientFile(filename, arrayBuffer) {
  const isXlsx = /\.xlsx?$/i.test(filename || '');
  const matrix = isXlsx ? xlsxToMatrix(arrayBuffer) : csvToMatrix(arrayBuffer);
  return parseSpreadsheetMatrix(matrix);
}
